import { ReactNode, SyntheticEvent, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { Menu } from 'primereact/menu'
import { TreeNode } from 'primereact/treenode'
import { TreeNodeTemplateOptions } from 'primereact/tree'
import { MenuItem } from 'primereact/menuitem'

import { PermissionSuffix } from '../../_generated/api/user-management'
import { useStructureContext } from '../contexts/StructureContext'
import { useUserContext } from '../contexts/UserContext'
import { Column, Row } from '../common/pure/FlexContainer'
import PrimeIcon from '../common/pure/PrimeIcon'
import {
    getMenuItemArchive,
    getMenuItemDuplicate,
    getMenuItemBoltConnection,
    getMenuItemProject,
    getMenuItemSeparator,
} from '../common/menu-items'
import DirectoryEntry from '../../models/DirectoryEntry'
import { StructureType, StructureTypes } from '../../models/Structure'
import ClassNameBuilder from '../../tools/ClassNameBuilder'
import MenuBuilder from '../../tools/MenuBuilder'
import { capture } from '../../tools/dom/event'
import t from '../../tools/t'
import CustomIcon from '../common/pure/CustomIcon'

interface StructureNodeProps {
    node: TreeNode
    options: TreeNodeTemplateOptions
    onAddChildNode: (node: TreeNode, type: StructureType) => void
    onDuplicateNode: (node: TreeNode) => void
    onDeleteNode: (node: TreeNode) => void
}
export default function StructureNode({
    node,
    options: _options,
    onAddChildNode,
    onDuplicateNode,
    onDeleteNode,
}: StructureNodeProps) {
    const { restoreStructure } = useStructureContext()
    const entry = node.data as DirectoryEntry
    const location = useLocation()
    // NOTE: Heuristic current state using location.
    const isCurrent = location.pathname.includes(entry?.id)
    const isArchived = entry.isArchived

    if (isArchived)
        return (
            <ArchiveNode
                label={node.label}
                isCurrent={isCurrent}
                onRestore={() => restoreStructure(entry)}
            />
        )

    switch (entry.referenceType) {
        case StructureTypes.Project:
            return (
                <ProjectNode
                    label={node.label}
                    isCurrent={isCurrent}
                    onAddChild={(type) => onAddChildNode(node, type)}
                    onDuplicate={node.data?.isRoot ? undefined : () => onDuplicateNode(node)}
                    onDelete={() => onDeleteNode(node)}
                />
            )

        case StructureTypes.BoltConnection:
            return (
                <BoltConnectionNode
                    label={node.label}
                    isCurrent={isCurrent}
                    onDuplicate={() => onDuplicateNode(node)}
                    onDelete={() => onDeleteNode(node)}
                />
            )
    }
}

interface ProjectNodeProps {
    label?: string
    isCurrent?: boolean
    onAddChild: (type: StructureType) => void
    onDelete: () => void
    onDuplicate?: () => void
}
const ProjectNode = ({ label, isCurrent, onAddChild, onDuplicate, onDelete }: ProjectNodeProps) => (
    <BaseTreeNode
        icon="folder"
        label={label}
        isCurrent={isCurrent}
        menu={new MenuBuilder()
            .add(getMenuItemProject(() => onAddChild(StructureTypes.Project), t('project_create')))
            .add(
                getMenuItemBoltConnection(
                    () => onAddChild(StructureTypes.BoltConnection),
                    t('bolt_connection_create')
                )
            )
            .add(onDuplicate ? getMenuItemDuplicate(onDuplicate) : undefined)
            .add(getMenuItemSeparator())
            .add(getMenuItemArchive(onDelete, t('project_delete')))
            .toArray()}
    />
)

interface BoltConnectionNodeProps {
    label?: string
    isCurrent?: boolean
    onDuplicate: () => void
    onDelete: () => void
}
const BoltConnectionNode = ({
    label,
    isCurrent,
    onDuplicate,
    onDelete,
}: BoltConnectionNodeProps) => (
    <BaseTreeNode
        iconNode={<CustomIcon name="bolt" />}
        label={label}
        isCurrent={isCurrent}
        menu={new MenuBuilder()
            .add(getMenuItemDuplicate(onDuplicate))
            .add(getMenuItemSeparator())
            .add(getMenuItemArchive(onDelete, t('bolt_connection_delete')))
            .toArray()}
    />
)

interface ArchiveNodeProps {
    label?: string
    isCurrent?: boolean
    onRestore: () => void
}
export const ArchiveNode = ({ label, isCurrent, onRestore }: ArchiveNodeProps) => (
    <BaseTreeNode
        icon="file"
        label={label}
        isCurrent={isCurrent}
        isArchived={true}
        menu={new MenuBuilder().add(getMenuItemDuplicate(onRestore, t('common_restore'))).toArray()}
    />
)

interface BaseTreeNodeProps {
    icon?: PrimeIconName
    iconNode?: ReactNode
    label?: string
    isCurrent?: boolean
    isArchived?: boolean
    menu: MenuItem[]
}
const BaseTreeNode = ({
    icon,
    iconNode,
    label,
    isCurrent,
    isArchived,
    menu,
}: BaseTreeNodeProps) => {
    const { can } = useUserContext()
    const classNames = new ClassNameBuilder()
        .add('structure-node')
        .add('hover-nest')
        .addIf('structure-node--current', isCurrent)
        .addIf('structure-node--archived', isArchived)
    return (
        <Row
            className={classNames.toString()}
            grow
            gap={0.5}
            arrangement="spaced"
            alignment="center"
        >
            <Row
                grow
                gap={0.5}
                alignment="center"
                arrangement="start"
                className="structure-node__text-container"
            >
                {icon && <PrimeIcon name={icon} />}
                {!!iconNode && iconNode}
                <div className="structure-node__text">{label}</div>
            </Row>

            {can(PermissionSuffix.StructureWrite) && <StructureNodeContextMenu menu={menu} />}
        </Row>
    )
}

interface StructureNodeContextMenuProps {
    menu: MenuItem[]
}
const StructureNodeContextMenu = ({ menu }: StructureNodeContextMenuProps) => {
    const ref = useRef<Menu>(null)
    const toggle = (event: SyntheticEvent) => ref.current?.toggle(event)
    return (
        <Column
            className="structure-node-context-menu hover-nest__target"
            arrangement="center"
            alignment="center"
            // Always `capture` context menu clicks because they should only ever open the given context menu,
            //  even when nested inside another clickable element.
            onClick={capture(toggle)}
        >
            <PrimeIcon name="ellipsis-v" size={16} />
            <Menu ref={ref} model={menu} popup />
        </Column>
    )
}
